import { useCallback, useEffect, useState } from "react";

import {
  consumeA2hsPendingAfterSignup,
  isIosLike,
  isStandaloneDisplay,
  markA2hsDone,
  markA2hsMaybeLater,
  markA2hsResurfaced,
  shouldResurfaceA2hsLight,
  shouldShowA2hsOnboarding,
} from "../utils/a2hs";
import "./AddToHomeScreen.css";

let deferredInstallPrompt = null;
let bipListenersAttached = false;

function ensureBeforeInstallPromptCapture() {
  if (typeof window === "undefined" || bipListenersAttached) return;
  bipListenersAttached = true;
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredInstallPrompt = event;
    window.dispatchEvent(new CustomEvent("a2hs-bip-ready"));
  });
  window.addEventListener("appinstalled", () => {
    deferredInstallPrompt = null;
    markA2hsDone();
  });
}

ensureBeforeInstallPromptCapture();

/**
 * Full-screen post-signup A2HS step (reference), plus a lighter resurface strip.
 * Skips entirely when already installed (display-mode: standalone).
 */
export default function AddToHomeScreen({ onVisibilityChange }) {
  const [phase, setPhase] = useState(null); // "full" | "light" | null
  const [bipReady, setBipReady] = useState(Boolean(deferredInstallPrompt));
  const ios = isIosLike();

  useEffect(() => {
    ensureBeforeInstallPromptCapture();
    const onReady = () => setBipReady(true);
    const onLight = () => {
      if (shouldResurfaceA2hsLight()) {
        markA2hsResurfaced();
        setPhase((prev) => prev || "light");
      }
    };
    window.addEventListener("a2hs-bip-ready", onReady);
    window.addEventListener("a2hs-request-light", onLight);
    return () => {
      window.removeEventListener("a2hs-bip-ready", onReady);
      window.removeEventListener("a2hs-request-light", onLight);
    };
  }, []);

  useEffect(() => {
    if (isStandaloneDisplay()) {
      setPhase(null);
      return;
    }
    if (shouldShowA2hsOnboarding()) {
      consumeA2hsPendingAfterSignup();
      setPhase("full");
    }
  }, []);

  useEffect(() => {
    onVisibilityChange?.(phase === "full");
  }, [phase, onVisibilityChange]);

  const finishDone = useCallback(() => {
    markA2hsDone();
    setPhase(null);
  }, []);

  const finishLater = useCallback(() => {
    markA2hsMaybeLater();
    setPhase(null);
  }, []);

  const handleInstallClick = async () => {
    const promptEvent = deferredInstallPrompt;
    if (!promptEvent) return;
    try {
      promptEvent.prompt();
      const choice = await promptEvent.userChoice;
      deferredInstallPrompt = null;
      setBipReady(false);
      if (choice?.outcome === "accepted") finishDone();
      else finishLater();
    } catch (err) {
      console.warn("A2HS prompt failed:", err);
    }
  };

  if (!phase) return null;

  if (phase === "light") {
    return (
      <div className="a2hs-light" role="dialog" aria-label="Add to home screen">
        <div className="a2hs-light-text">
          <strong>Add 100ViewsOnly to your home screen</strong>
          <span>So Pip can reach you when a drop is about to fill.</span>
        </div>
        <div className="a2hs-light-actions">
          {ios ? (
            <button type="button" className="a2hs-btn a2hs-btn-gold" onClick={() => setPhase("full")}>
              Show me how
            </button>
          ) : (
            <button
              type="button"
              className="a2hs-btn a2hs-btn-gold"
              onClick={handleInstallClick}
              disabled={!bipReady && !deferredInstallPrompt}
            >
              Add
            </button>
          )}
          <button type="button" className="a2hs-btn a2hs-btn-ghost" onClick={finishLater}>
            Not now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="a2hs-page" role="dialog" aria-modal="true" aria-label="Add to home screen">
      <div className="a2hs-inner">
        <div className="a2hs-brand">100VO</div>
        <h1 className="a2hs-title">Before we get into it, let&apos;s put us on your home screen</h1>
        <p className="a2hs-sub">
          So we&apos;re always one tap away, and so Pip can let you know when a drop is about to fill.
        </p>

        {ios ? (
          <>
            <div className="a2hs-step">
              <div className="a2hs-icon" aria-hidden="true">↑</div>
              <div>
                <div className="a2hs-step-title">Tap the Share button</div>
                <div className="a2hs-step-desc">At the bottom of Safari</div>
              </div>
            </div>
            <div className="a2hs-step">
              <div className="a2hs-icon" aria-hidden="true">▢</div>
              <div>
                <div className="a2hs-step-title">Choose &quot;Add to Home Screen&quot;</div>
                <div className="a2hs-step-desc">Scroll down in the share sheet</div>
              </div>
            </div>
            <div className="a2hs-step">
              <div className="a2hs-icon a2hs-icon-solid" aria-hidden="true">100</div>
              <div>
                <div className="a2hs-step-title">Tap &quot;Add&quot;</div>
                <div className="a2hs-step-desc">Gold icon appears on your home screen</div>
              </div>
            </div>
            <button type="button" className="a2hs-btn a2hs-btn-gold a2hs-btn-block" onClick={finishDone}>
              I&apos;ve added it
            </button>
          </>
        ) : (
          <>
            <div className="a2hs-step">
              <div className="a2hs-icon a2hs-icon-solid" aria-hidden="true">100</div>
              <div>
                <div className="a2hs-step-title">100ViewsOnly</div>
                <div className="a2hs-step-desc">Made in Canada. Watched everywhere.</div>
              </div>
            </div>
            <button
              type="button"
              className="a2hs-btn a2hs-btn-gold a2hs-btn-block"
              onClick={handleInstallClick}
            >
              Add to home screen
            </button>
            {!bipReady && (
              <p className="a2hs-hint">
                If the install dialog doesn&apos;t appear yet, wait a moment or use your browser&apos;s Install option.
              </p>
            )}
          </>
        )}

        <button type="button" className="a2hs-btn a2hs-btn-ghost a2hs-btn-block" onClick={finishLater}>
          Maybe later
        </button>
      </div>
    </div>
  );
}

/** Fire when a push-related moment is about to happen (permission bar / inbound push). */
export function requestA2hsResurface() {
  if (!shouldResurfaceA2hsLight()) return false;
  window.dispatchEvent(new CustomEvent("a2hs-request-light"));
  return true;
}
